import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { agentDetailPath } from './agent-route.util';
import { FormsModule } from '@angular/forms';
import { filter, finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgApexchartsModule } from 'ng-apexcharts';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgentApiService } from 'src/app/core/services/agent-api.service';
import type {
  AgentRecord,
  AgentVersionChangeType,
  AgentVersionRecord,
} from 'src/app/core/services/agent-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { TenantRow } from 'src/app/core/services/tenant-onboarding-api.service';
import { CREATE_AGENT_AGENT_QUERY_PARAM } from 'src/app/pages/manage-agent/create-agent/create-agent.paths';
import { PRODUCT_SLUG_AGENTIC_PD } from 'src/app/shared/utils/tenant-onboarding-catalog';
import { navigateCreateAgentSegment } from 'src/app/pages/manage-agent/create-agent/create-agent-navigation';
import type { PageSurfaceTab } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import {
  DeleteConfirmModalComponent,
  type DeleteConfirmVariant,
} from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { ManageAgentTableComponent } from './manage-agent-table/manage-agent-table.component';
import {
  agentLifecycleAction,
  lifecycleConfirmCopy,
  mapAgentListStatusFilter,
} from './agent-lifecycle.util';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import {
  ConfirmActionDialogComponent,
  type ConfirmActionDialogData,
} from 'src/app/pages/manage-client/confirm-action-dialog.component';

type ManageAgentTab = 'published' | 'draft' | 'deleted';

@Component({
  selector: 'app-manage-agent',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    NgApexchartsModule,
    IconModule,
    NgScrollbarModule,
    MatDialogModule,
    HasPermissionPipe,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ManageAgentTableComponent,
    RestoreConfirmModalComponent,
    DeleteConfirmModalComponent,
  ],
  templateUrl: './manage-agent.component.html',
  styleUrl: './manage-agent.component.css'
})
export class ManageAgentComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly router = inject(Router);
  private readonly agentApi = inject(AgentApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly permissionService = inject(PermissionService);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search agents…' },
      status: {
        options: [
          { value: '', label: 'All statuses' },
          { value: 'ACTIVE', label: 'Active' },
          { value: 'INACTIVE', label: 'Paused' },
          { value: 'DEPLOYED', label: 'Deployed' },
        ],
      },
      dateRange: { placeholder: 'Date range' },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  /**
   * Client filter for the list.
   *
   * Kept beside listFilters rather than inside it: ListModuleFilters is the shared toolbar's own
   * shape, and widening it would push a client concept onto every list that uses the toolbar.
   */
  clientFilterId: number | null = null;
  clients: TenantRow[] = [];

  openActionMenuKey: string | null = null;
  manageAgentTab: ManageAgentTab = 'published';

  /** Stable reference — do not use a getter (new array each CD breaks tab clicks). */
  agentSurfaceTabs: PageSurfaceTab[] = [
    { key: 'published', label: 'Published' },
    { key: 'draft', label: 'Draft' },
    { key: 'deleted', label: 'Deleted' }
  ];

  agentImage = 'assets/images/agent-image.png';

  agents: AgentRecord[] = [];
  page = 1;
  readonly pageSize = 10;
  meta: { total: number; lastPage: number; currentPage: number; perPage: number } | null = null;
  loading = false;
  listError = '';

  // action state
  actionLoading = false;
  actionError = '';

  ngOnInit(): void {
    this.agentSurfaceTabs = this.buildAgentSurfaceTabs();
    this.manageAgentTab = this.resolveInitialAgentTab();
    this.loadClients();
    this.loadAgents();
  }

  /**
   * Every client, for the filter dropdown.
   *
   * Loaded once on entry rather than per keystroke — the list is small and static next to the
   * agent list it filters.
   */
  private loadClients(): void {
    this.tenantApi
      .listTenants({ page: 1, page_size: 500 })
      .subscribe({ next: (res) => (this.clients = res?.data?.result ?? []) });
  }

  clientLabel(t: TenantRow): string {
    const name = t.tenant_name ?? t.tenant_subdomain ?? `Client #${t.id}`;
    return t.tenant_name && t.tenant_subdomain ? `${name} — ${t.tenant_subdomain}` : name;
  }

  onClientFilterChange(): void {
    // Page 3 of every client's agents is usually past the end of one client's, which would show an
    // empty table for a filter that does match rows.
    this.page = 1;
    this.loadAgents();
  }

  clearClientFilter(): void {
    this.clientFilterId = null;
    this.onClientFilterChange();
  }

  private buildAgentSurfaceTabs(): PageSurfaceTab[] {
    const tabs: PageSurfaceTab[] = [];
    if (this.permissionService.isAccessAllowed(this.P.AGENT_LIST)) {
      tabs.push({ key: 'published', label: 'Published' });
    }
    if (this.permissionService.isAccessAllowed(this.P.AGENT_LIST)) {
      tabs.push({ key: 'draft', label: 'Draft' });
    }
    if (this.permissionService.isAccessAllowed(this.P.AGENT_ARCHIVED_LIST)) {
      tabs.push({ key: 'deleted', label: 'Deleted' });
    }
    return tabs.length ? tabs : [{ key: 'published', label: 'Published' }];
  }

  private resolveInitialAgentTab(): ManageAgentTab {
    const keys = new Set(this.agentSurfaceTabs.map((t) => t.key));
    if (keys.has(this.manageAgentTab)) {
      return this.manageAgentTab;
    }
    return (this.agentSurfaceTabs[0]?.key as ManageAgentTab) ?? 'published';
  }

  loadAgents(): void {
    this.loading = true;
    this.listError = '';
    this.agentApi
      .listAgents({
        tab: this.manageAgentTab,
        tenant_id: this.clientFilterId ?? undefined,
        status: mapAgentListStatusFilter(this.listFilters.status),
        search: this.listFilters.searchQuery.trim() || undefined,
        date_from: this.listFilters.dateRangeStart || undefined,
        date_to: this.listFilters.dateRangeEnd || undefined,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.agents = res.data.items;
            const total = res.data.total ?? 0;
            const currentPage = res.data.page ?? this.page;
            const perPage = res.data.page_size ?? this.pageSize;
            this.page = currentPage;
            this.meta = {
              total,
              currentPage,
              perPage,
              lastPage: Math.max(1, Math.ceil(total / perPage)),
            };
          } else {
            this.agents = [];
            this.meta = null;
          }
        },
        error: () => {
          this.agents = [];
          this.meta = null;
          this.listError = 'Could not load agents. Please refresh.';
        },
      });
  }

  onTabChange(tab: 'published' | 'draft' | 'deleted'): void {
    if (tab === this.manageAgentTab) {
      return;
    }
    if (tab === 'draft' && !this.permissionService.isAccessAllowed(this.P.AGENT_LIST)) {
      this.snackBar.open('You do not have permission to view draft agents.', 'Close', {
        duration: 5000,
      });
      return;
    }
    if (tab === 'deleted' && !this.permissionService.isAccessAllowed(this.P.AGENT_ARCHIVED_LIST)) {
      this.snackBar.open('You do not have permission to view deleted agents.', 'Close', {
        duration: 5000,
      });
      return;
    }
    if (tab === 'published' && !this.permissionService.isAccessAllowed(this.P.AGENT_LIST)) {
      this.snackBar.open('You do not have permission to view published agents.', 'Close', {
        duration: 5000,
      });
      return;
    }
    this.closeActionMenu();
    this.closeRestoreModal();
    this.closeDeleteModal();
    this.manageAgentTab = tab;
    this.page = 1;
    this.listFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
    this.clientFilterId = null;
    this.loadAgents();
  }

  onSurfaceTabKeyChange(key: string): void {
    if (key === 'published' || key === 'draft' || key === 'deleted') {
      this.onTabChange(key);
    }
  }

  get emptyMessage(): string {
    return this.listError || 'No agents found.';
  }

  get serialOffset(): number {
    const m = this.meta;
    return m ? (m.currentPage - 1) * m.perPage : 0;
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.loadAgents();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.loadAgents();
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  goToAgentDetail(row: AgentRecord): void {
    const path = agentDetailPath(row);
    if (path) {
      void this.router.navigate(path);
      return;
    }
    this.snackBar.open('This agent is missing a profile link.', 'Dismiss', { duration: 5000 });
  }

  onViewDetails(row: AgentRecord): void {
    this.closeActionMenu();
    this.goToAgentDetail(row);
  }

  onRetrain(row: AgentRecord): void {
    this.closeActionMenu();
    this.router.navigate(['/create-agent', 'agent-details'], {
      queryParams: { [CREATE_AGENT_AGENT_QUERY_PARAM]: row.uuid },
    });
  }

  onPause(row: AgentRecord): void {
    this.closeActionMenu();
    const label = row.agent_code ?? row.name ?? 'this agent';
    const copy = lifecycleConfirmCopy(row.status, label);
    if (!copy || !agentLifecycleAction(row.status)) {
      this.snackBar.open(
        'Only live agents can be paused; deployed or paused agents can go live or resume.',
        'Dismiss',
        { duration: 5000 },
      );
      return;
    }
    const dialogData: ConfirmActionDialogData = {
      title: copy.title,
      message: copy.message,
      confirmLabel: copy.confirmLabel,
      confirmColor: copy.confirmColor,
    };
    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '440px',
      data: dialogData,
      disableClose: true,
    });
    ref
      .afterClosed()
      .pipe(filter((ok) => ok === true))
      .subscribe(() => this.submitToggleStatus(row));
  }

  private submitToggleStatus(row: AgentRecord): void {
    this.actionError = '';
    this.agentApi
      .toggleStatus(row.uuid)
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (env) => {
          const msg = env.message?.trim() || 'Agent status updated.';
          this.snackBar.open(msg, 'Dismiss', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
          if (env.data) {
            this.patchAgentInList(env.data);
          } else {
            this.loadAgents();
          }
        },
        error: (err) => {
          const apiMsg = (err as { error?: { message?: string } })?.error?.message;
          this.actionError = apiMsg ?? 'Could not update agent status.';
          this.snackBar.open(this.actionError, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
  }

  /** Apply toggle/delete response to the visible list without waiting for a full reload. */
  private patchAgentInList(updated: AgentRecord): void {
    const idx = this.agents.findIndex(
      (a) =>
        (updated.uuid && a.uuid === updated.uuid) ||
        (updated.id != null && a.id === updated.id),
    );
    if (idx < 0) {
      this.loadAgents();
      return;
    }
    this.agents = this.agents.map((a, i) => (i === idx ? { ...a, ...updated } : a));
  }

  showDeleteModal = false;
  deleteLoading = signal(false);
  deleteError = '';
  deleteVariant: DeleteConfirmVariant = 'soft-delete';
  selectedAgentForDelete: AgentRecord | null = null;

  onDelete(row: AgentRecord): void {
    this.confirmDelete(row);
  }

  confirmDelete(row: AgentRecord): void {
    this.closeActionMenu();
    this.deleteError = '';
    this.deleteVariant = 'soft-delete';
    this.selectedAgentForDelete = row;
    this.showDeleteModal = true;
  }

  confirmPermanentDelete(row: AgentRecord): void {
    this.closeActionMenu();
    this.deleteError = '';
    this.deleteVariant = 'permanent';
    this.selectedAgentForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedAgentForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedAgentForDelete;
    if (!row) return;
    if (this.deleteVariant === 'permanent') {
      this.runPermanentDelete(row);
      return;
    }
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.actionError = '';
    this.agentApi
      .deleteAgent(row.uuid)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: () => {
          this.snackBar.open('Agent removed successfully.', 'Dismiss', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
          this.closeDeleteModal();
          this.loadAgents();
        },
        error: () => {
          this.deleteError = 'Could not remove agent.';
          this.snackBar.open(this.deleteError, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
  }

  private runPermanentDelete(row: AgentRecord): void {
    this.deleteError = '';
    this.deleteLoading.set(true);
    this.actionError = '';
    this.agentApi
      .permanentDeleteAgent(row.uuid)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (res) => {
          const msg =
            (res as { message?: string })?.message ?? 'Agent permanently deleted.';
          this.snackBar.open(msg, 'Dismiss', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
          this.closeDeleteModal();
          if (this.agents.length <= 1 && this.page > 1) {
            this.page -= 1;
          }
          this.loadAgents();
        },
        error: (err) => {
          const apiMsg = (err as { error?: { message?: string } })?.error?.message;
          this.deleteError = apiMsg ?? 'Could not permanently delete agent.';
          this.snackBar.open(this.deleteError, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
  }

  get selectedAgentDeleteName(): string {
    return this.selectedAgentForDelete?.name ?? '';
  }

  get deleteModalHeading(): string {
    return this.deleteVariant === 'permanent' ? 'Delete agent permanently?' : 'Remove agent?';
  }

  showRestoreModal = false;
  restoreLoading = signal(false);
  selectedAgent: AgentRecord | null = null;

  menuRestore(row: AgentRecord): void {
    this.confirmRestore(row);
  }

  confirmRestore(row: AgentRecord): void {
    this.closeActionMenu();
    this.selectedAgent = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedAgent = null;
  }

  submitRestore(): void {
    if (!this.selectedAgent) return;
    this.restoreLoading.set(true);
    this.actionError = '';
    this.agentApi
      .restoreAgent(this.selectedAgent.uuid)
      .pipe(finalize(() => this.restoreLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.snackBar.open(
            (res as { message?: string })?.message ?? 'Agent restored successfully.',
            'Dismiss',
            { duration: 4000, horizontalPosition: 'end', verticalPosition: 'top' },
          );
          this.closeRestoreModal();
          this.loadAgents();
        },
        error: () => {
          this.actionError = 'Could not restore agent.';
          this.snackBar.open('Could not restore agent.', 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
          this.closeRestoreModal();
        },
      });
  }

  // ── Clone / Assign to Tenant modal ──────────────────────────────────────────

  // ── Step 1: select target client ─────────────────────────────────────────────
  assignModalOpen = false;
  assignStep: 1 | 2 = 1;
  assignSourceAgent: AgentRecord | null = null;
  assignTenants: TenantRow[] = [];
  /** Product id used for the last successful tenant fetch (only clients with this product). */
  private assignTenantsLoadedForProductId: number | null = null;
  assignTenantsLoading = false;
  assignSelectedTenantId: number | null = null;
  assignTenantSearch = '';
  assignError = '';
  assignSuccessCode = '';

  // ── Step 2: customise the clone ───────────────────────────────────────────────
  cloneName = '';
  cloneCustomerType: string = '';
  cloneSubProfile: string = '';
  cloneCopyQuestions = true;
  cloneCopyDocuments = true;
  cloneCopySimulate = true;
  cloneNameError = false;

  // ── Loading ───────────────────────────────────────────────────────────────────
  assignLoading = false;

  // ── Versioning modal ────────────────────────────────────────────────────────
  versionModalOpen = false;
  versionSourceAgent: AgentRecord | null = null;
  versionChangeType: AgentVersionChangeType = 'MINOR';
  versionNotes = '';
  versionMajorName = '';
  versionLoading = false;
  versionError = '';
  versionResult: { version: string; agent_code: string | null; isMajor: boolean } | null = null;

  // ── Version history modal ────────────────────────────────────────────────────
  historyModalOpen = false;
  historySourceAgent: AgentRecord | null = null;
  historyVersions: AgentVersionRecord[] = [];
  historyLoading = false;

  get assignFilteredTenants(): TenantRow[] {
    const q = this.assignTenantSearch.trim().toLowerCase();
    const sourceTenantId = this.assignSourceAgent?.tenant_id ?? null;
    const all = q
      ? this.assignTenants.filter(
        (t) =>
          (t.tenant_name ?? '').toLowerCase().includes(q) ||
          (t.tenant_subdomain ?? '').toLowerCase().includes(q) ||
          (t.email ?? '').toLowerCase().includes(q),
      )
      : this.assignTenants;
    // Sort: current client (already assigned) goes to the bottom
    return [...all].sort((a, b) => {
      const aIsSource = a.id === sourceTenantId ? 1 : 0;
      const bIsSource = b.id === sourceTenantId ? 1 : 0;
      return aIsSource - bIsSource;
    });
  }

  /** True when the source agent product is Agentic PD (customer type / sub-profile apply). */
  get assignSourceIsPd(): boolean {
    return (this.assignSourceAgent?.product?.slug ?? '').trim() === PRODUCT_SLUG_AGENTIC_PD;
  }

  onAssignToTenant(row: AgentRecord): void {
    this.closeActionMenu();
    this.assignSourceAgent = row;
    this.assignStep = 1;
    this.assignSelectedTenantId = null;
    this.assignTenantSearch = '';
    this.assignError = '';
    this.assignSuccessCode = '';
    // Pre-fill step-2 fields from source
    this.cloneName = row.name ?? '';
    this.cloneCustomerType = row.customer_type ?? '';
    this.cloneSubProfile = row.customer_sub_profile ?? '';
    this.cloneCopyQuestions = true;
    this.cloneCopyDocuments = true;
    this.cloneCopySimulate = true;
    this.cloneNameError = false;
    this.assignModalOpen = true;
    this.loadTenantsForAssign();
  }

  private loadTenantsForAssign(): void {
    const productId =
      this.assignSourceAgent?.product_id ?? this.assignSourceAgent?.product?.id ?? null;
    if (productId == null || productId <= 0) {
      this.assignTenants = [];
      this.assignTenantsLoadedForProductId = null;
      this.assignError = 'Source agent has no product. Cannot list clients.';
      return;
    }
    if (
      this.assignTenantsLoadedForProductId === productId &&
      this.assignTenants.length > 0
    ) {
      return;
    }
    this.assignTenantsLoading = true;
    this.assignError = '';
    this.tenantApi
      .listTenants({ page: 1, page_size: 200, product_id: productId })
      .pipe(finalize(() => (this.assignTenantsLoading = false)))
      .subscribe({
        next: (res) => {
          this.assignTenants = res.data?.result ?? [];
          this.assignTenantsLoadedForProductId = productId;
          if (
            this.assignSelectedTenantId != null &&
            !this.assignTenants.some((t) => t.id === this.assignSelectedTenantId)
          ) {
            this.assignSelectedTenantId = null;
          }
        },
        error: () => {
          this.assignError = 'Could not load client list.';
        },
      });
  }

  assignGoToStep2(): void {
    if (!this.assignSelectedTenantId) return;
    this.assignError = '';
    this.cloneNameError = false;
    this.assignStep = 2;
  }

  assignGoToStep1(): void {
    this.assignStep = 1;
    this.assignError = '';
    this.cloneNameError = false;
  }

  confirmAssign(): void {
    const agent = this.assignSourceAgent;
    const tenantId = this.assignSelectedTenantId;
    if (!agent || !tenantId) return;

    const nameVal = this.cloneName.trim();
    if (!nameVal) {
      this.cloneNameError = true;
      return;
    }
    this.cloneNameError = false;
    this.assignLoading = true;
    this.assignError = '';
    this.assignSuccessCode = '';

    const isPd = this.assignSourceIsPd;
    this.agentApi
      .cloneAgent(agent.uuid, {
        tenant_id: tenantId,
        name: nameVal,
        ...(isPd && this.cloneCustomerType
          ? { customer_type: this.cloneCustomerType as 'salaried' | 'self-employed' | 'self-employed-np' }
          : {}),
        ...(isPd && this.cloneSubProfile
          ? { customer_sub_profile: this.cloneSubProfile as 'bank' | 'cash' }
          : {}),
        copy_questions: this.cloneCopyQuestions,
        copy_documents: this.cloneCopyDocuments,
        copy_simulate: this.cloneCopySimulate,
      })
      .pipe(finalize(() => (this.assignLoading = false)))
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.assignSuccessCode = res.data.agent_code ?? 'new agent';
          }
          this.loadAgents();
        },
        error: (err) => {
          this.assignError =
            (err as { error?: { message?: string } })?.error?.message ??
            'Clone failed. Please try again.';
        },
      });
  }

  closeAssignModal(): void {
    this.assignModalOpen = false;
    this.assignSourceAgent = null;
    this.assignSuccessCode = '';
    this.assignError = '';
    this.assignStep = 1;
  }

  // ── Versioning ──────────────────────────────────────────────────────────────

  onCreateVersion(row: AgentRecord): void {
    this.versionSourceAgent = row;
    this.versionChangeType = 'MINOR';
    this.versionNotes = '';
    this.versionMajorName = '';
    this.versionError = '';
    this.versionResult = null;
    this.versionModalOpen = true;
  }

  closeVersionModal(): void {
    this.versionModalOpen = false;
    this.versionSourceAgent = null;
    this.versionResult = null;
    this.versionError = '';
  }

  submitVersion(): void {
    const agent = this.versionSourceAgent;
    if (!agent) return;
    this.versionLoading = true;
    this.versionError = '';

    const isMajor = this.versionChangeType === 'MAJOR';
    this.agentApi
      .createAgentVersion(agent.uuid, {
        change_type: this.versionChangeType,
        ...(this.versionNotes.trim() ? { notes: this.versionNotes.trim() } : {}),
        ...(isMajor && this.versionMajorName.trim() ? { name: this.versionMajorName.trim() } : {}),
      })
      .pipe(finalize(() => (this.versionLoading = false)))
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.versionResult = {
              version: res.data.version,
              agent_code: res.data.agent_code ?? null,
              isMajor,
            };
          }
          this.loadAgents();
        },
        error: (err) => {
          this.versionError =
            (err as { error?: { message?: string } })?.error?.message ??
            'Could not publish version. Please try again.';
        },
      });
  }

  openVersionHistory(row: AgentRecord): void {
    this.historySourceAgent = row;
    this.historyVersions = [];
    this.historyLoading = true;
    this.historyModalOpen = true;
    this.agentApi
      .listAgentVersions(row.uuid)
      .pipe(finalize(() => (this.historyLoading = false)))
      .subscribe({
        next: (res) => (this.historyVersions = res?.data ?? []),
        error: () => (this.historyVersions = []),
      });
  }

  closeVersionHistory(): void {
    this.historyModalOpen = false;
    this.historySourceAgent = null;
    this.historyVersions = [];
  }

  navigateToCreateAgent(): void {
    const nav = navigateCreateAgentSegment('agent-details', null);
    void this.router.navigate(nav.commands, nav.extras);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (t.closest('.agent-action-menu')) return;
    this.openActionMenuKey = null;
  }
}
